import ENDPOINTS from '@/config/apiEndpoints';
import APP_CONFIG from '@/config/app';
import { logDebug, logError, logInfo } from '@/utils/logger';
type AuthStatusCallback = (isAuthorized: boolean) => void;
class AuthorizationServiceClass {
  private _isAuthorized = true;
  private _subscribers: Set<AuthStatusCallback> = new Set();
  public getAuthorizationStatus(): boolean {
    logDebug(
      'Getting authorization status',
      'AuthorizationService.getAuthorizationStatus',
      {
        isAuthorized: this._isAuthorized,
      },
    );
    return this._isAuthorized;
  }
  public setUnauthorized(): void {
    if (!this._isAuthorized) {
      return;
    }
    logInfo(
      'Setting user as unauthorized for beta access',
      'AuthorizationService.setUnauthorized',
    );
    this._isAuthorized = false;
    this._notifySubscribers();
  }
  public setAuthorized(): void {
    if (this._isAuthorized) {
      return;
    }
    logInfo(
      'Setting user as authorized for beta access',
      'AuthorizationService.setAuthorized',
    );
    this._isAuthorized = true;
    this._notifySubscribers();
  }
  public async checkAuthorizationStatus(): Promise<boolean> {
    const caller = 'AuthorizationService.checkAuthorizationStatus';
    logInfo('Checking authorization status', caller);
    try {
      logInfo('Making direct API call to wallets endpoint', caller);
      const baseUrl = APP_CONFIG.API_SERVICE.BASE_URL || '';
      const endpoint = ENDPOINTS.USER_WALLETS;
      let url =
        baseUrl.endsWith('/') && endpoint.startsWith('/')
          ? `${baseUrl}${endpoint.substring(1)}`
          : `${baseUrl}${endpoint}`;
      if (url.endsWith('/')) {
        url = url.slice(0, -1);
      }
      const authToken = sessionStorage.getItem('authToken');
      if (!authToken) {
        logInfo('No auth token found, user is not authenticated', caller);
        return false;
      }
      logInfo(`Making fetch request to ${url}`, caller, {
        baseUrl,
        endpoint,
        fullUrl: url,
      });
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });
      logInfo(`Received response with status ${response.status}`, caller);
      if (response.ok) {
        logInfo('User is authorized for beta access (200 response)', caller);
        if (!this._isAuthorized) {
          logInfo(
            'Updating authorization status from unauthorized to authorized',
            caller,
          );
          this.setAuthorized();
          return true;
        }
        return false;
      } else if (response.status === 401) {
        try {
          const errorData = await response.json();
          logInfo('Received 401 error response', caller, { errorData });
          if (
            errorData.errorCode === 'UNAUTHORIZED' &&
            errorData.errorMessage === 'Not part of beta program'
          ) {
            logInfo(
              'User is not authorized for beta access (401 response)',
              caller,
            );
            if (this._isAuthorized) {
              logInfo(
                'Updating authorization status from authorized to unauthorized',
                caller,
              );
              this.setUnauthorized();
              return true;
            }
            return false;
          }
        } catch (parseError) {
          logError(parseError, `${caller}.parseError`, {
            message: 'Error parsing 401 response',
            status: response.status,
          });
        }
      }
      logInfo(`Unexpected response status: ${response.status}`, caller);
      return false;
    } catch (error) {
      logError(error, caller, {
        message: 'Error checking authorization status',
      });
      return false;
    }
  }
  public subscribe(callback: AuthStatusCallback): () => void {
    this._subscribers.add(callback);
    callback(this._isAuthorized);
    return () => this.unsubscribe(callback);
  }
  public unsubscribe(callback: AuthStatusCallback): void {
    this._subscribers.delete(callback);
  }
  private _notifySubscribers(): void {
    this._subscribers.forEach((callback) => {
      try {
        callback(this._isAuthorized);
      } catch (error) {
        logError(error, 'AuthorizationService._notifySubscribers', {
          subscriberCount: this._subscribers.size,
        });
      }
    });
  }
}
const AuthorizationService = new AuthorizationServiceClass();
export default AuthorizationService;
